import React from "react";
import {
  Paper,
  Box,
  Typography,
  Switch,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Divider,
} from "@mui/material";
import { PrivacySettings } from "shared/types";

interface PrivacySectionProps {
  settings: PrivacySettings;
  onChange: (key: keyof PrivacySettings, value: string | boolean) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#178B8E",
  },
};

const SwitchRow: React.FC<{
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}> = ({ label, description, checked, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.5,
    }}
  >
    <Box>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size="small"
      sx={switchSx}
    />
  </Box>
);

const SelectRow: React.FC<{
  label: string;
  description?: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ label, description, value, options, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      py: 1.5,
      gap: 2,
    }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight={500}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

const PrivacySection: React.FC<PrivacySectionProps> = ({
  settings,
  onChange,
}) => {
  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        Privacy
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Control who can see your information
      </Typography>

      <Stack divider={<Divider />}>
        <SelectRow
          label="Profile Visibility"
          description="Who can see your profile"
          value={settings.profileVisibility}
          options={[
            { value: "public", label: "Public — All sitters can see" },
            {
              value: "private",
              label: "Private — Only sitters I've contacted",
            },
            { value: "hidden", label: "Hidden — Not searchable" },
          ]}
          onChange={(v) => onChange("profileVisibility", v)}
        />
        <SwitchRow
          label="Show in search results"
          description="Allow sitters to find you in search"
          checked={settings.showInSearch}
          onChange={(v) => onChange("showInSearch", v)}
        />
        <SelectRow
          label="Booking history visibility"
          value={settings.bookingHistory}
          options={[
            { value: "all", label: "Visible to all sitters" },
            { value: "booked", label: "Visible to sitters I've booked" },
            { value: "hidden", label: "Hidden from all" },
          ]}
          onChange={(v) => onChange("bookingHistory", v)}
        />
        <SwitchRow
          label="Show last active status"
          description="Let sitters see when you were last online"
          checked={settings.showLastActive}
          onChange={(v) => onChange("showLastActive", v)}
        />
        <SelectRow
          label="Phone number visibility"
          value={settings.phoneVisibility}
          options={[
            { value: "all", label: "Show to all sitters" },
            { value: "after-booking", label: "Show after booking confirmed" },
            { value: "never", label: "Never show (app messaging only)" },
          ]}
          onChange={(v) => onChange("phoneVisibility", v)}
        />
      </Stack>
    </Paper>
  );
};

export default PrivacySection;
